import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InlineProgress } from './budget-progress-bar';
import { StatusDot } from './budget-health-indicator';
import { formatCurrency, formatPercent, formatVariance } from '@/lib/budget-utils';
import type { BudgetStatus } from '@/types/budget.types';

interface BudgetTableRow {
  id: number;
  name?: string;
  budget?: number | string;
  actual?: number | string;
  committed?: number | string;
  available?: number | string;
  variance?: number | string;
  percent?: number | string;
  status?: BudgetStatus | 'completed' | 'in-progress' | 'not-started' | 'complete' | React.ReactNode;
  children?: BudgetTableRow[];
  isExpandable?: boolean;
  // Allow any additional properties for flexible row structures
  [key: string]: unknown;
}

interface ColumnConfig {
  showCommitted?: boolean;
  showAvailable?: boolean;
  showVariance?: boolean;
  showPercent?: boolean;
  showStatus?: boolean;
}

interface ColumnDefinition {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center' | string;
}

interface BudgetTableProps {
  data: BudgetTableRow[];
  columns?: ColumnConfig | ColumnDefinition[];
  variant?: 'default' | 'compact' | 'simple' | 'expandable';
  totalRow?: Record<string, unknown>;
}

export function BudgetTable({ 
  data, 
  columns = {
    showCommitted: false,
    showAvailable: true,
    showVariance: false,
    showPercent: true,
    showStatus: false,
  },
  variant = 'default',
  totalRow,
}: BudgetTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Check if columns is an array of column definitions
  const isColumnDefinitionArray = Array.isArray(columns);

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // If using column definition array, render a simple table with custom columns
  if (isColumnDefinitionArray) {
    const columnDefs = columns as ColumnDefinition[];
    const cellPadding = variant === 'compact' ? 'py-2' : 'py-3';
    
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {columnDefs.map((col, index) => (
                <TableHead 
                  key={col.key} 
                  className={`${cellPadding} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                >
                  {col.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id}>
                {columnDefs.map((col) => {
                  const cellValue = row[col.key];
                  return (
                    <TableCell 
                      key={col.key}
                      className={`${cellPadding} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                    >
                      {cellValue as React.ReactNode}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            {totalRow && (
              <TableRow className="font-bold bg-muted/50">
                {columnDefs.map((col) => {
                  const cellValue = totalRow[col.key];
                  return (
                    <TableCell 
                      key={col.key}
                      className={`${cellPadding} ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}
                    >
                      {cellValue as React.ReactNode}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    );
  }

  // Original ColumnConfig-based rendering
  const columnConfig = columns as ColumnConfig;

  const renderRow = (row: BudgetTableRow, level: number = 0): React.ReactNode => {
    const isExpanded = expandedRows.has(row.id);
    const hasChildren = row.children && row.children.length > 0;
    const paddingLeft = level * 24;

    return (
      <>
        <TableRow key={row.id} className={level > 0 ? 'bg-muted/50' : ''}>
          <TableCell style={{ paddingLeft: `${paddingLeft + 16}px` }}>
            <div className="flex items-center gap-2">
              {hasChildren && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => toggleRow(row.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              )}
              {!hasChildren && <span className="w-6" />}
              <span className={level === 0 ? 'font-medium' : ''}>{row.name}</span>
            </div>
          </TableCell>
          <TableCell className="text-right">{typeof row.budget === 'string' ? row.budget : formatCurrency(row.budget)}</TableCell>
          <TableCell className="text-right">{typeof row.actual === 'string' ? row.actual : formatCurrency(row.actual)}</TableCell>
          {columnConfig.showCommitted && (
            <TableCell className="text-right">
              {row.committed ? (typeof row.committed === 'string' ? row.committed : formatCurrency(row.committed)) : '-'}
            </TableCell>
          )}
          {columnConfig.showAvailable && (
            <TableCell className="text-right">
              {row.available !== undefined ? (typeof row.available === 'string' ? row.available : formatCurrency(row.available)) : '-'}
            </TableCell>
          )}
          {columnConfig.showVariance && (
            <TableCell className="text-right">
              {row.variance !== undefined ? (typeof row.variance === 'string' ? row.variance : formatVariance(row.variance, 'currency')) : '-'}
            </TableCell>
          )}
          {columnConfig.showPercent && (
            <TableCell className="text-right">
              {row.percent !== undefined && row.status ? (
                <InlineProgress percent={typeof row.percent === 'string' ? parseFloat(row.percent) : row.percent} status={row.status as BudgetStatus} />
              ) : (
                <span>{row.percent !== undefined ? (typeof row.percent === 'string' ? row.percent : formatPercent(row.percent)) : '-'}</span>
              )}
            </TableCell>
          )}
          {columnConfig.showStatus && (
            <TableCell>
              {row.status && <StatusDot status={row.status as BudgetStatus} />}
            </TableCell>
          )}
        </TableRow>
        {hasChildren && isExpanded && row.children?.map((child) => renderRow(child, level + 1))}
      </>
    );
  };

  const cellPadding = variant === 'compact' ? 'py-2' : 'py-3';

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={cellPadding}>Category</TableHead>
            <TableHead className={`${cellPadding} text-right`}>Budget</TableHead>
            <TableHead className={`${cellPadding} text-right`}>Actual</TableHead>
            {columnConfig.showCommitted && (
              <TableHead className={`${cellPadding} text-right`}>Committed</TableHead>
            )}
            {columnConfig.showAvailable && (
              <TableHead className={`${cellPadding} text-right`}>Available</TableHead>
            )}
            {columnConfig.showVariance && (
              <TableHead className={`${cellPadding} text-right`}>Variance</TableHead>
            )}
            {columnConfig.showPercent && (
              <TableHead className={`${cellPadding} text-right`}>Progress</TableHead>
            )}
            {columnConfig.showStatus && (
              <TableHead className={cellPadding}>Status</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row) => renderRow(row, 0))}
        </TableBody>
      </Table>
    </div>
  );
}

// Simple table variant for phase/person breakdowns
interface SimpleBudgetTableProps {
  headers: string[];
  rows: (string | number | React.ReactNode)[][];
  variant?: 'default' | 'compact';
}

export function SimpleBudgetTable({ headers, rows, variant = 'default' }: SimpleBudgetTableProps) {
  const cellPadding = variant === 'compact' ? 'py-2' : 'py-3';

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {headers.map((header, index) => (
              <TableHead 
                key={index} 
                className={`${cellPadding} ${index > 0 ? 'text-right' : ''}`}
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell 
                  key={cellIndex}
                  className={`${cellPadding} ${cellIndex > 0 ? 'text-right' : ''}`}
                >
                  {typeof cell === 'number' ? formatCurrency(cell) : cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
